import React from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search, X } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// PICKER — the console's rich select.
//
// WHY A NEW COMPONENT RATHER THAN A BETTER `Select`. `ui/Form.jsx`'s `Select` wraps a native
// `<select>`, and it is used in 233 places. A native select is genuinely the right control for
// three short options: it is one line, it is accessible for free, and on a phone it opens the
// platform's own wheel. What it cannot do is search, multi-select, show a hint under an option,
// explain why an option is unavailable, or look the same on Windows as it does here. So this is a
// SIBLING, not a replacement — reach for it when you need one of those, and leave the trivial ones
// alone.
//
// It follows `ui/Dropdown.jsx`'s positioning approach on purpose: a body portal with fixed
// coordinates re-measured on scroll and resize, so the list escapes the `overflow:hidden` wrappers
// that every table in this console sits inside. Dropdown itself could not be extended — it is a
// menu of actions, with no value, no selection and no listbox semantics.
//
//   <Picker value={x} onChange={setX} options={['UGX','KES']} placeholder="Currency: All" />
//   <Picker multiple value={ids} onChange={setIds} options={events} searchable />
//   <Picker options={tiers} disabled disabledReason="No line-level owner exists yet." />
//
// OPTIONS are strings, or objects:
//   { value, label, hint?, group?, icon?, disabled?, disabledReason? }
// `hint` is a second line — the thing a native <option> has no room for, and the reason half the
// selects in this console are followed by a paragraph of explanatory text.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * SIZES. Added because the console's compact filter bars run at h-7 and this shipped at h-9 only,
 * so every dense toolbar had to keep a native <select> to stay aligned — which is how a component
 * meant to replace primitives ends up sitting beside them instead.
 *
 * The LIST is deliberately not scaled with the trigger: options stay at a readable size whatever
 * the control that opens them. A 10px dropdown is not a smaller control, it is a worse one.
 */
const TRIGGER_SIZES = {
  // `xs` for the dense GRIDS (not toolbars) — the brand cards run design rows at h-6 inside a
  // three-column grid, so anything taller re-flows the whole card. Added as a size rather than
  // let callers pass `className`, which lands on the WRAPPER and silently fails to override the
  // trigger: a caller "styling it smaller" would just get sm with no indication why.
  xs: 'min-h-6 px-1.5 text-[10.5px]',
  sm: 'min-h-7 px-2 text-[11.5px]',
  // The h-8 list toolbars — Orders, where it sits beside FieldSearchBox (also h-8).
  bar: 'min-h-8 px-2.5 text-[12px]',
  md: 'min-h-9 px-3 text-[12.5px]',
};

/** Strings and objects both arrive here; everything below deals only in the object form. */
const normalize = (options) =>
  (options || []).map((o) =>
    typeof o === 'string' || typeof o === 'number'
      ? { value: String(o), label: String(o) }
      : { ...o, value: String(o.value), label: o.label ?? String(o.value) },
  );

/** Case- and accent-insensitive contains. `localeCompare` is for ordering, not matching. */
const matches = (opt, q) => {
  if (!q) return true;
  const hay = `${opt.label} ${opt.hint || ''} ${opt.value}`.toLowerCase();
  return hay.includes(q.toLowerCase());
};

export const Picker = ({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  /** Show a search box. Defaults to ON past ten options — the point where scanning stops working. */
  searchable,
  multiple = false,
  /** Offer an × to return to the empty value. Not shown when there is nothing to clear. */
  clearable = false,
  disabled = false,
  /** Why this whole control is unavailable. Rendered as the title, so it survives a hover. */
  disabledReason,
  /** Shown in the list when a search matches nothing, or there are no options at all. */
  emptyText = 'Nothing matches',
  loading = false,
  /**
   * Called the first time this picker OPENS — so a caller can fetch its options on demand instead
   * of on mount. A list that only matters once somebody decides to change something should not be
   * on the page's critical path: the fixture card was fetching all 80 teams on every event view to
   * fill a dropdown most visits never open, and when that fetch was slow enough to be aborted the
   * picker had no option matching its value and displayed the raw UUID.
   */
  onOpen,
  /**
   * Told what is typed in the search box, so a caller can add matches from the server — for a list too
   * big to send whole (every event ever). What it adds is filtered like any option.
   */
  onSearch,
  /** Pills beyond this collapse into "+N". Keeps a multi-select from eating the toolbar. */
  maxPills = 3,
  size = 'md',
  /**
   * 'field' (default) looks like an input. 'ghost' has no border or fill until you hover it —
   * for a control that reads as an ACTION in a list ("+ add a category override…") rather than a
   * value someone set. Offered as a variant because callers cannot restyle the trigger: className
   * lands on the wrapper, so a caller trying to strip the border would silently keep it.
   * 'bare' is a segment of another control (the "by name" of a search box, the "More…" of a period
   * strip): no border or fill in any state, the host draws the chrome; it fills the segment's height.
   */
  variant = 'field',
  className = '',
  /** On the wrapper, beside className (Select's native-width minimum). */
  style,
  ariaLabel,
  id,
  /**
   * WHAT THIS CONTROL CHOOSES, said in the trigger in every state — "Paid with  Mobile money",
   * "Rail  Flutterwave". Without it a filter read "Payment" until used and then just the value
   * ("Pending" — status or rail?), so the control never said what it was and what it was set to
   * at the same time. Muted, so the value stays the thing you read.
   */
  lead,
  /** The kind of thing chosen, drawn before `lead`. A chosen option's own `icon` replaces it. */
  icon: LeadIcon,
}) => {
  const opts = React.useMemo(() => normalize(options), [options]);
  const canSearch = searchable ?? opts.length > 10;

  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState(null);
  const [q, setQ] = React.useState('');
  const [activeIdx, setActiveIdx] = React.useState(0);

  const triggerRef = React.useRef(null);
  const listRef = React.useRef(null);
  const searchRef = React.useRef(null);
  const optionRefs = React.useRef([]);

  const selected = multiple ? (Array.isArray(value) ? value.map(String) : []) : value == null ? '' : String(value);
  const isSelected = (v) => (multiple ? selected.includes(v) : selected === v);

  const visible = React.useMemo(() => opts.filter((o) => matches(o, q)), [opts, q]);
  React.useEffect(() => { onSearch?.(q); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  // Selectable rows only — arrow keys must not park on a disabled option, and Enter on one would
  // do nothing, which reads as a broken keyboard.
  const selectableIdx = React.useMemo(
    () => visible.reduce((acc, o, i) => (o.disabled ? acc : [...acc, i]), []),
    [visible],
  );

  // ── Position, the same way Dropdown does it, plus a flip.
  //
  // A filter bar sits near the top and a table's last row sits near the bottom; a list that always
  // opens downward is unusable in the second case. Measure the room and open upward when the
  // list would otherwise be clipped by the viewport.
  const place = React.useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const MAX = 320;
    const below = window.innerHeight - r.bottom - 8;
    const above = r.top - 8;
    const up = below < Math.min(MAX, 200) && above > below;
    setPos({
      left: Math.round(r.left),
      width: Math.round(r.width),
      ...(up ? { bottom: Math.round(window.innerHeight - r.top + 4) } : { top: Math.round(r.bottom + 4) }),
      maxHeight: Math.min(MAX, Math.max(160, up ? above : below)),
    });
  }, []);

  React.useEffect(() => {
    if (!open) return undefined;
    place();
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target) || listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    // `true` — capture. A scroll inside a table wrapper does not bubble, and the list would
    // otherwise hang in place while the row it belongs to slides away.
    // Escape is bound to the DOCUMENT, not just to the control. The trigger's own onKeyDown only
    // fires while focus is inside, and focus can legitimately be elsewhere — clicking a pill's ×
    // leaves it where it was — which stranded an open list with no way to dismiss it but a click.
    // CAPTURE, and claimed (preventDefault): Escape closes the innermost open thing only — the list, not the
    // drawer or dialog around it, whose own Escape handler leaves a claimed key alone.
    const onEsc = (e) => { if (e.key === 'Escape') { e.preventDefault(); setOpen(false); triggerRef.current?.focus(); } };
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc, true);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc, true);
    };
  }, [open, place]);

  // Opening resets the query and parks the cursor on what is already chosen, so Enter re-confirms
  // rather than silently picking the first row.
  React.useEffect(() => {
    if (!open) return;
    setQ('');
    const cur = visible.findIndex((o) => isSelected(o.value) && !o.disabled);
    setActiveIdx(cur >= 0 ? cur : selectableIdx[0] ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // FOCUS THE SEARCH BOX ONLY ONCE THE PORTAL EXISTS. The first render after `open` has no
  // position yet, so the portal — and the input — are not mounted; focusing from the effect above
  // was a no-op against a null ref, and typing went to the page instead of the filter. Keyed on
  // `ready` rather than `pos` so a scroll, which re-measures, does not steal focus back mid-type.
  const ready = open && !!pos;
  React.useEffect(() => {
    if (ready && canSearch) searchRef.current?.focus();
  }, [ready, canSearch]);

  // THE CURSOR IS DERIVED, NOT SYNCHRONISED.
  //
  // A filtered list can leave `activeIdx` pointing at a row that no longer exists, so it has to
  // fall back. Doing that in an effect keyed on the query was a bug with teeth: opening the picker
  // reset the query, the reset fired that effect, and it overwrote the "park on the current
  // selection" that the open effect had just set — so Enter on a freshly-opened picker silently
  // chose the FIRST option instead of confirming the one already selected.
  //
  // Clamping during render has no such ordering to get wrong: an index that is still selectable is
  // kept, and anything else falls to the first selectable row.
  const safeActive = selectableIdx.includes(activeIdx) ? activeIdx : (selectableIdx[0] ?? -1);

  React.useEffect(() => {
    if (open) optionRefs.current[safeActive]?.scrollIntoView({ block: 'nearest' });
  }, [safeActive, open]);

  const commit = (opt) => {
    if (opt.disabled) return;
    if (multiple) {
      const next = isSelected(opt.value) ? selected.filter((v) => v !== opt.value) : [...selected, opt.value];
      onChange?.(next);
      // The list STAYS OPEN on a multi-select — picking three things should not mean opening the
      // same list three times.
    } else {
      onChange?.(opt.value);
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  const step = (dir) => {
    if (!selectableIdx.length) return;
    const at = selectableIdx.indexOf(safeActive);
    const nextPos = at === -1 ? 0 : (at + dir + selectableIdx.length) % selectableIdx.length;
    setActiveIdx(selectableIdx[nextPos]);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) { e.preventDefault(); onOpen?.(); setOpen(true); }
      return;
    }
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); step(1); break;
      case 'ArrowUp': e.preventDefault(); step(-1); break;
      case 'Home': e.preventDefault(); setActiveIdx(selectableIdx[0] ?? 0); break;
      case 'End': e.preventDefault(); setActiveIdx(selectableIdx[selectableIdx.length - 1] ?? 0); break;
      case 'Enter': e.preventDefault(); if (visible[safeActive]) commit(visible[safeActive]); break;
      case 'Escape': e.preventDefault(); setOpen(false); triggerRef.current?.focus(); break;
      case 'Tab': setOpen(false); break;
      case 'Backspace':
        // Only when the search box is empty, so backspace still edits the query first.
        if (multiple && !q && selected.length) onChange?.(selected.slice(0, -1));
        break;
      default: break;
    }
  };

  const chosen = multiple ? opts.filter((o) => selected.includes(o.value)) : opts.find((o) => o.value === selected);
  const hasValue = multiple ? selected.length > 0 : !!selected;

  const groups = React.useMemo(() => {
    const out = [];
    let last = null;
    visible.forEach((o, i) => {
      if ((o.group || null) !== last) { last = o.group || null; out.push({ header: last, i }); }
      out.push({ opt: o, i });
    });
    return out;
  }, [visible]);

  return (
    <div className={`relative ${className}`} style={style}>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        aria-label={ariaLabel || placeholder}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((v) => { if (!v) onOpen?.(); return !v; })}
        onKeyDown={onKeyDown}
        className={`w-full border text-left flex items-center gap-1.5 transition-colors ${variant === 'bare' ? 'h-full py-0 rounded-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1F2937]/40' : 'rounded-md'} ${variant === 'bare' ? '' : 'py-1'} ${TRIGGER_SIZES[size] || TRIGGER_SIZES.md} ${
          disabled
            ? 'border-[#EEEDEA] bg-[#F5F4F1] text-[#9CA3AF] cursor-not-allowed'
            : variant === 'bare'
              ? 'border-transparent bg-transparent text-[#4B5563] hover:text-[#1F2937]'
            : variant === 'ghost'
              ? open
                ? 'border-[#E6E4E1] bg-white text-[#1F2937]'
                : 'border-transparent bg-transparent text-[#6B7280] hover:bg-[#F6F5F2] hover:text-[#1F2937]'
              : open
                ? 'border-[#1F2937] bg-white text-[#1F2937]'
                : 'border-[#E6E4E1] bg-white text-[#1F2937] hover:border-[#D6D3CE]'
        }`}
      >
        {(() => {
          const Icon = (!multiple && chosen?.icon) || LeadIcon;
          return Icon ? <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${hasValue && !multiple && chosen?.icon ? 'text-[#1F2937]' : 'text-[#9CA3AF]'}`} strokeWidth={1.75} /> : null;
        })()}
        {lead && <span className="flex-shrink-0 text-[#9CA3AF] whitespace-nowrap">{lead}</span>}
        <span className="flex-1 min-w-0 flex items-center gap-1 flex-wrap py-0.5">
          {!hasValue && <span className="text-[#9CA3AF]">{placeholder}</span>}

          {/* MULTI-SELECT AS PILLS. Each carries its own ×, because the alternative — reopening the
              list and hunting for the row to untick — is how a five-item selection becomes a chore. */}
          {multiple && chosen.slice(0, maxPills).map((o) => (
            <span key={o.value} className="inline-flex items-center gap-1 rounded-full bg-[#F2F1ED] pl-2 pr-1 py-0.5 text-[11.5px] text-[#1F2937]">
              {o.label}
              <span
                role="button"
                tabIndex={-1}
                aria-label={`Remove ${o.label}`}
                onClick={(e) => { e.stopPropagation(); onChange?.(selected.filter((v) => v !== o.value)); }}
                className="rounded-full p-0.5 hover:bg-[#E6E4E1]"
              >
                <X className="w-2.5 h-2.5" />
              </span>
            </span>
          ))}
          {multiple && chosen.length > maxPills && (
            <span className="text-[11.5px] text-[#6B7280]">+{chosen.length - maxPills}</span>
          )}

          {!multiple && hasValue && (
            <span className="truncate">{chosen?.label ?? selected}</span>
          )}
        </span>

        {clearable && hasValue && !disabled && (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Clear"
            onClick={(e) => { e.stopPropagation(); onChange?.(multiple ? [] : ''); }}
            className="rounded p-0.5 text-[#9CA3AF] hover:bg-[#F2F1ED] hover:text-[#1F2937]"
          >
            <X className="w-3.5 h-3.5" />
          </span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 ${disabled ? 'text-[#C9C7C2]' : 'text-[#6B7280]'} ${open ? 'rotate-180' : ''} transition-transform`} />
      </button>

      {/* The list is as wide as its options need (a name cut to "Blacks Power FC vs Vip…" cannot be chosen by
          reading it), never narrower than its trigger, at most 360px unless the trigger is wider, kept on screen. */}
      {open && pos && createPortal(
        <div
          ref={listRef}
          data-portal-popper=""

          style={{ position: 'fixed', left: Math.max(8, Math.min(pos.left, window.innerWidth - 368)), top: pos.top, bottom: pos.bottom, minWidth: Math.max(pos.width, 200), width: 'max-content', maxWidth: Math.max(pos.width, 360) }}
          className="z-[120] rounded-md border border-[#E6E4E1] bg-white shadow-lg overflow-hidden"
        >
          {canSearch && (
            <div className="relative border-b border-[#EEEDEA]">
              <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                ref={searchRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search"
                aria-label="Search options"
                className={`w-full h-8 pl-8 ${q ? 'pr-7' : 'pr-2'} text-[12.5px] outline-none placeholder:text-[#9CA3AF]`}
              />
              {/* The same quiet × every search box has (ui/SearchInput); this one sits inside the
                  menu, so it keeps the menu open and the cursor in the filter. */}
              {q && (
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { setQ(''); searchRef.current?.focus(); }} aria-label="Clear search" title="Clear"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 inline-flex items-center justify-center rounded text-[#9CA3AF] hover:text-[#4B5563] hover:bg-[#F2F1ED]">
                  <X className="w-3 h-3" strokeWidth={2.25} />
                </button>
              )}
            </div>
          )}

          <div role="listbox" aria-multiselectable={multiple} style={{ maxHeight: pos.maxHeight }} className="overflow-y-auto py-1">
            {loading && <div className="px-3 py-4 text-[12px] text-[#9CA3AF]">Loading…</div>}
            {!loading && !visible.length && <div className="px-3 py-4 text-[12px] text-[#9CA3AF]">{emptyText}</div>}

            {!loading && groups.map((row, k) =>
              row.header !== undefined ? (
                row.header && (
                  <div key={`h${k}`} className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.06em] text-[#9CA3AF]">
                    {row.header}
                  </div>
                )
              ) : (
                <div
                  key={row.opt.value}
                  ref={(el) => { optionRefs.current[row.i] = el; }}
                  role="option"
                  aria-selected={isSelected(row.opt.value)}
                  aria-disabled={!!row.opt.disabled}
                  // A DISABLED OPTION IS STILL LISTED, with its reason on hover — the same rule the
                  // rest of this console follows. Hiding it teaches nobody that it exists.
                  title={row.opt.disabled ? row.opt.disabledReason : undefined}
                  onMouseEnter={() => !row.opt.disabled && setActiveIdx(row.i)}
                  onClick={() => commit(row.opt)}
                  className={`px-3 py-1.5 flex items-start gap-2 text-[12.5px] ${
                    row.opt.disabled
                      ? 'text-[#C9C7C2] cursor-not-allowed'
                      : `cursor-pointer ${row.i === safeActive ? 'bg-[#F2F1ED]' : ''}`
                  }`}
                >
                  <span className="w-3.5 flex-shrink-0 pt-[3px]">
                    {isSelected(row.opt.value) && <Check className="w-3.5 h-3.5 text-[#1F2937]" />}
                  </span>
                  {row.opt.icon && <row.opt.icon className="w-3.5 h-3.5 flex-shrink-0 mt-[3px] text-[#6B7280]" strokeWidth={1.75} />}
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate ${isSelected(row.opt.value) ? 'font-medium text-[#1F2937]' : ''}`}>
                      {row.opt.label}
                    </span>
                    {(row.opt.hint || (row.opt.disabled && row.opt.disabledReason)) && (
                      <span className="block text-[11px] text-[#9CA3AF] leading-snug">
                        {row.opt.disabled ? row.opt.disabledReason : row.opt.hint}
                      </span>
                    )}
                  </span>
                </div>
              ),
            )}
          </div>

          {multiple && (
            <div className="flex items-center justify-between border-t border-[#EEEDEA] px-2 py-1.5">
              <span className="text-[11px] text-[#9CA3AF]">{selected.length} selected</span>
              <button
                type="button"
                onClick={() => onChange?.([])}
                disabled={!selected.length}
                className={`h-6 px-2 rounded text-[11.5px] ${selected.length ? 'text-[#1F2937] hover:bg-[#F2F1ED]' : 'text-[#C9C7C2] cursor-not-allowed'}`}
              >
                Clear all
              </button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};
